/**
 * New modular App component - Clean architecture with Authentication
 */
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AppProvider } from './context/AppContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import ViewRouter from './components/ViewRouter';
import LoginForm from './components/auth/LoginForm';
import CSVUploadModal from './components/modals/CSVUploadModal';
import AddInterviewModal from './components/modals/AddInterviewModal';
import PreviewPage from './components/public/PreviewPage';
import RelocationNotice from './components/common/RelocationNotice';
import './App.css';

// Protected App Component
const ProtectedApp = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading KeyCrew CMS...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return (
    <ErrorBoundary>
      <AppProvider>
        <Layout>
          <Suspense fallback={<LoadingSpinner fullScreen text="Loading application..." />}>
            <ViewRouter />
          </Suspense>
        </Layout>
        
        {/* Global Modals */}
        <CSVUploadModal />
        <AddInterviewModal />
      </AppProvider>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        {/* Public, read-only client preview — outside auth/password gate.
            DELIBERATELY OUTSIDE the relocation notice too: these are links already in clients'
            inboxes. Blocking or redirecting them would break something outward-facing for people
            who have no idea the team moved servers, to solve a problem that is purely internal. */}
        <Route path="/preview/:token" element={<PreviewPage />} />
        <Route
          path="/*"
          element={
            <AuthProvider>
              {/* Above ProtectedApp so it covers the LOGIN screen as well — someone who cannot
                  sign in is exactly who needs telling the app has moved. */}
              <RelocationNotice />
              <ProtectedApp />
            </AuthProvider>
          }
        />
      </Routes>
    </Router>
  );
}

export default App;